import classNames from 'classnames';
import React from 'react';
import {
  components,
  ControlProps,
  MenuProps,
  OptionProps,
  ValueContainerProps,
} from 'react-select';
import {
  MultiValueGenericProps,
  MultiValueRemoveProps,
} from 'react-select/src/components/MultiValue';
import CreatableSelect from 'react-select/creatable';
import { SelectProps } from './select';
import { XIcon } from '@heroicons/react/solid';

export interface SelectChipsProps extends SelectProps {
  createLabel?: string;
  isMulti?: boolean;
  isValidNewOption?: (args: any) => any;
}

type ChipColor = 'neutral' | 'secondary';

const MultiValueContainer = (
  props: MultiValueGenericProps<{
    label: string;
    value: any;
    chipColor: ChipColor;
    hidden: boolean;
  }>,
) => {
  props.innerProps.className = classNames(
    'px-3 rounded-full flex h-6 items-center mr-2.5 my-1',
    {
      'bg-neutral-100':
        props.data.chipColor === 'neutral' || !props.data.chipColor,
      'bg-violet-400': props.data.chipColor === 'secondary',
      hidden: props.data.hidden,
    },
  );
  return <components.MultiValueContainer {...props} />;
};

const MultiValueLabel = (
  props: MultiValueGenericProps<{
    label: string;
    value: any;
    chipColor: ChipColor;
  }>,
) => {
  props.innerProps.className = classNames('text-xs', {
    'text-white': props.data.chipColor === 'secondary',
  });
  return (
    <components.MultiValueLabel
      {...props}
      innerProps={Object.assign({}, props.innerProps, {
        'data-cy': 'multi-value-label',
      })}
    />
  );
};

const ValueContainer = ({
  children,
  ...props
}: ValueContainerProps<any, any>) => {
  return (
    <components.ValueContainer {...props}>{children}</components.ValueContainer>
  );
};

const MultiValueRemove = (props: MultiValueRemoveProps<any>) => {
  props.innerProps.className =
    'hover:bg-none hover:cursor-pointer flex items-center';
  return (
    <components.MultiValueRemove {...props}>
      <XIcon
        className={classNames('h-3 w-3', {
          'text-white': props.data.chipColor === 'secondary',
        })}
      />
    </components.MultiValueRemove>
  );
};

const Control = ({ children, ...props }: ControlProps<any, any>) => {
  props.className = classNames(
    'flex items-center border bg-white outline-none rounded-md px-2 py-1.5 cursor-pointer w-full text-sm',
    { 'border-black': props.isFocused, 'border-neutral-300': !props.isFocused },
  );
  const dataCy = props.selectProps['dataCy'];
  return (
    <components.Control
      {...props}
      innerProps={Object.assign({}, props.innerProps, { 'data-cy': dataCy })}
    >
      {children}
    </components.Control>
  );
};

const Option = (props: OptionProps<any, any>) => {
  return (
    <components.Option
      {...props}
      className="cursor-pointer select-none text-sm relative px-4 h-10 flex items-center outline-none hover:bg-hover-secondary focus:bg-focus-secondary transition"
    />
  );
};

const Menu = (props: MenuProps<any, any>) => {
  return (
    <components.Menu
      {...props}
      innerProps={Object.assign({}, props.innerProps, {
        'data-cy': 'select-dropdown',
      })}
    />
  );
};

const Input = (props: any) => {
  return (
    <div style={{}}>
      <div className="top-1 left-1 absolute pointer-events-none text-sm text-[#808080]">
        {props.selectProps?.selected?.length &&
        props.selectProps?.selected?.every((el: any) => el.hidden) &&
        !props.value &&
        props.selectProps?.placeholder
          ? props.selectProps.placeholder
          : ''}
      </div>
      <components.Input {...props} />
    </div>
  );
};

const customStyles = {
  valueContainer: (provided: any) => ({
    ...provided,
    padding: 0,
  }),
  option: () => ({
    fontSize: '14px',
  }),

  control: () => ({}),
};

const theme = (theme: any) => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary: '#DCD1FF',
    primary75: '#F2F0FF',
    primary50: '#F2F0FF',
    primary25: '#F2F0FF',
  },
});

export const SelectChips = (props: SelectChipsProps) => {
  const TheCreatableSelect: any = CreatableSelect;

  return (
    <TheCreatableSelect
      options={props.data}
      isMulti={props.isMulti ?? true}
      onChange={props.onChange}
      formatCreateLabel={(inputValue: string) => {
        return (
          <div>
            {props.createLabel ? props.createLabel + ' ' : 'Create new '} "
            {inputValue}"
          </div>
        );
      }}
      components={{
        MultiValueContainer: MultiValueContainer,
        MultiValueLabel: MultiValueLabel,
        MultiValueRemove: MultiValueRemove,
        Menu: Menu,
        Control: Control,
        IndicatorSeparator: () => null,
        DropdownIndicator: () => null,
        Option: Option,
        ValueContainer: ValueContainer,
        Input: Input,
      }}
      isSearchable={true}
      isClearable={false}
      theme={theme}
      styles={customStyles}
      data-cy={props.dataCy}
      {...props}
    />
  );
};

SelectChips.filePath = 'libs/shared/ui-kit/src/lib/select/select-chips.tsx';
